import * as React from 'react';
import { Translation, useTranslation } from 'react-i18next';

import { CheckIcon, XIcon } from '@/components/Elements/Icons';
import { PRODUCT_FREE, PRODUCT_LITE, PRODUCT_PRO, PRODUCT_TOTAL } from '@/config/appConstants';

import features from './features.json';

export type FeatureComparatorProps = {
  className?: string;
};

export const FeatureComparator = ({ className }: FeatureComparatorProps) => {
  const { t } = useTranslation();

  const okImage = () => <CheckIcon className="h-7 w-7 text-green-700 m-auto" aria-hidden="true" />;

  const koImage = () => <XIcon className="h-7 text-md-red m-auto" aria-hidden="true" />;

  const parseFeature = (feature: boolean | string | number) => {
    if (typeof feature === 'boolean') {
      return <span className="relative left-2.5 lg:left-0">{feature ? okImage() : koImage()}</span>;
    }

    if (typeof feature === 'string') {
      return (
        <span className="text-sm lg:text-lg">
          <Translation>{(t) => t(`FeatureComparator.${feature}`)}</Translation>
        </span>
      );
    }

    return <span className="text-sm lg:text-lg">{feature}</span>;
  };

  const renderBlock = (block: any, index: number) => {
    const { title, features } = block;

    return (
      <div key={`block--${index.toString()}`} className="my-14">
        <div className="text-left font-black text-xl uppercase w-full bg-md-silver underline uppercase p-4 text-md-black">
          {t(`FeatureComparator.${title}`)}
        </div>
        {features.map(renderFeature)}
      </div>
    );
  };

  const renderFeature = (feature: any, index: number) => {
    const { title, specs } = feature;
    const { free, lite, pro, total } = specs;

    return (
      <div
        key={`feature--${index.toString()}`}
        className="flex lg:justify-between m-5 pt-2 pb-8 border-b-2 border-dashed border-md-silver font-secondary items-center flex-wrap lg:flex-nowrap"
      >
        <div className="w-full lg:w-1/5 text-left mb-5 lg:mb-0 underline lg:no-underline">
          {t(`FeatureComparator.${title}`)}
        </div>
        <div className="w-full lg:w-1/5 text-center flex align-middle justify-between lg:justify-center items-center">
          <span className="lg:hidden pr-2 w-24 text-left font-black lg:pr-0">
            MD {PRODUCT_FREE}:
          </span>
          {parseFeature(free)}
        </div>
        <div className="w-full lg:w-1/5 text-center flex align-middle justify-between lg:justify-center items-center">
          <span className="lg:hidden pr-2 w-24 text-left font-black lg:pr-0">
            MD {PRODUCT_LITE}:
          </span>
          {parseFeature(lite)}
        </div>
        <div className="w-full lg:w-1/5 text-center flex align-middle justify-between lg:justify-center items-center">
          <span className="lg:hidden pr-2 w-24 text-left font-black lg:pr-0">
            MD {PRODUCT_PRO}:
          </span>
          {parseFeature(pro)}
        </div>
        <div className="w-full lg:w-1/5 text-center flex align-middle justify-between lg:justify-center items-center">
          <span className="lg:hidden pr-2 w-24 text-left font-black lg:pr-0">
            MD {PRODUCT_TOTAL}:
          </span>
          {parseFeature(total)}
        </div>
      </div>
    );
  };

  return (
    <section className={className}>
      <div className="w-9/10 lg:w-4/5 my-12 m-auto overflow">
        <div>
          <div className="m-7 hidden lg:flex">
            <div className="w-full lg:w-1/5 text-center">
              <div className="text-center font-black text-2xl uppercase tracking-tighter text-md-purple">
                MD {PRODUCT_FREE}
              </div>
            </div>
            <div className="w-full lg:w-1/5 text-center">
              <div className="text-center font-black text-2xl uppercase tracking-tighter text-md-purple">
                MD {PRODUCT_LITE}
              </div>
            </div>
            <div className="w-full lg:w-1/5 text-center">
              <div className="text-center font-black text-2xl uppercase tracking-tighter text-md-purple">
                MD {PRODUCT_PRO}
              </div>
            </div>
            <div className="w-full lg:w-1/5 text-center">
              <div className="text-center font-black text-2xl uppercase tracking-tighter text-md-purple">
                MD {PRODUCT_TOTAL}
              </div>
            </div>
          </div>
          {features.map(renderBlock)}
        </div>
      </div>
    </section>
  );
};
